import { goto } from '$app/navigation';
import ROUTE_IDS, { type TRouteId } from '$src/generated/routeIds';
import type { NativePayloads, NavigationPayloads, Payload } from '@casumo/fabric-fundamentals';
import { spineBus } from '../gatekeeper/MessageBus';
import { navigate } from '../navigation';
import { GATEKEEPER_VERSION } from '../gatekeeper';
import { getRouteFromPathname } from '../navigation/getRouteFromPathname';
import { camelCase } from 'lodash-es';
import { get } from 'svelte/store';
import { page } from '$app/stores';

const NATIVE_ROUTE_IDS = {
    'about-us': ROUTE_IDS['/about-casumo'],
    // game_details: ROUTE_IDS.GAME_DETAILS, // TODO
    'live-casino': ROUTE_IDS.LIVE_CASINO,
    // mahjong: ROUTE_IDS.MAHJONG_PAGE, // TODO:
    faq: ROUTE_IDS.FAQ,
    play: ROUTE_IDS.PLAY,
    practice: ROUTE_IDS.PRACTICE,
    // games: ROUTE_IDS.GAMES,  // NOTE: THIS IS AN ALIAS OF GAMES_TOP -- NATIVE EXPECTS GAMES_TOP as "homepage" id.
    'games-search': ROUTE_IDS.GAMES_SEARCH,
    // 'games-provider': ROUTE_IDS.GAME_PROVIDER_GAMES,
    // 'must-drop-jackpots': ROUTE_IDS.MUST_DROP_JACKPOTS,
    'games-top': ROUTE_IDS.TOP_LISTS,
    promotions: ROUTE_IDS.PROMOTIONS,
    'promotions-detail': ROUTE_IDS.PROMOTION_DETAILS,
    // 'jackpot-detail': ROUTE_IDS.JACKPOTS_DETAILS,
    // 'jackpots-explainer': ROUTE_IDS.JACKPOT_EXPLAINER,
    player: ROUTE_IDS.PLAYER_DASHBOARD,
    // 'reel-races': ROUTE_IDS.REEL_RACES,
    // 'about-reel-races': ROUTE_IDS.REEL_RACES_PROMOPAGE,
    'about-adventure': ROUTE_IDS.ADVENTURE_PROMOPAGE,
    settings: ROUTE_IDS.PLAYER_SETTINGS,
    // 'account-details': ROUTE_IDS.PLAYER_SETTINGS_ACCOUNT_DETAILS,
    // notifications: ROUTE_IDS.PLAYER_SETTINGS_NOTIFICATIONS,
    // 'phone-number-change': ROUTE_IDS.PHONE_NUMBER_VERIFICATION,
    deposit: ROUTE_IDS.CASH_DEPOSIT,
    withdraw: ROUTE_IDS.CASH_WITHDRAW,
    history: ROUTE_IDS.TRANSACTION_HISTORY,
    'history-bets': ROUTE_IDS.TRANSACTION_HISTORY_BETS,
    // 'history-transactions-annual-overview': ROUTE_IDS.TRANSACTION_ANNUAL_OVERVIEW,
    sports: ROUTE_IDS.SPORTS,
    'play-okay': ROUTE_IDS.PLAY_OKAY,
    'play-okay-settings': ROUTE_IDS.PLAYER_PLAY_OKAY_SETTINGS,
    // 'play-okay-settings-review': ROUTE_IDS.PLAYER_PLAY_OKAY_SETTINGS_REVIEW,
    'documents-verification': ROUTE_IDS.ACCOUNT_VERIFICATION,
    'terms-and-conditions': ROUTE_IDS.TERMS_AND_CONDITIONS,
    'cookies-notice': ROUTE_IDS['/cookies-notice'],
    // 'bonus-terms-and-conditions': ROUTE_IDS.BONUS_TERMS_AND_CONDITIONS,
    'privacy-policy': ROUTE_IDS.PRIVACY_POLICY,
    'payments-redirection-callback': ROUTE_IDS.PAYMENTS_REDIRECTION_CALLBACK,
    'payments-pay-and-play': ROUTE_IDS.PAYMENTS_PAY_AND_PLAY,
    more: ROUTE_IDS.MORE,
    'hall-of-fame': ROUTE_IDS.HALL_OF_FAME,
    'games-slots': ROUTE_IDS.GAMES_SLOTS
};

const KNOWN_ROUTES: Record<string, TRouteId> = {
    '/spel/top': ROUTE_IDS.GAMES_TOP,
    '/games/top': ROUTE_IDS.GAMES_TOP,
    '/spillvelger/top': ROUTE_IDS.GAMES_TOP,
    '/pelit/top': ROUTE_IDS.GAMES_TOP,
    '/spiele/top': ROUTE_IDS.GAMES_TOP,
    '/spel/all': ROUTE_IDS.GAMES,
    '/games/all': ROUTE_IDS.GAMES,
    '/spillvelger/all': ROUTE_IDS.GAMES,
    '/pelit/all': ROUTE_IDS.GAMES,
    '/spiele/all': ROUTE_IDS.GAMES,
    '/spel/search': ROUTE_IDS.GAMES_SEARCH,
    '/games/search': ROUTE_IDS.GAMES_SEARCH,
    '/spillvelger/search': ROUTE_IDS.GAMES_SEARCH,
    '/pelit/search': ROUTE_IDS.GAMES_SEARCH,
    '/spiele/search': ROUTE_IDS.GAMES_SEARCH,
    '/spel/slots': ROUTE_IDS.GAMES_SLOTS,
    '/games/slots': ROUTE_IDS.GAMES_SLOTS,
    '/spillvelger/slots': ROUTE_IDS.GAMES_SLOTS,
    '/pelit/slots': ROUTE_IDS.GAMES_SLOTS,
    '/spiele/slots': ROUTE_IDS.GAMES_SLOTS,
    '/spel': ROUTE_IDS.GAMES,
    '/games': ROUTE_IDS.GAMES,
    '/spillvelger': ROUTE_IDS.GAMES,
    '/pelit': ROUTE_IDS.GAMES,
    '/spiele': ROUTE_IDS.GAMES
};

const findNativeRouteId = (routeId: string) =>
    Object.entries(NATIVE_ROUTE_IDS).find(([, value]) => value === routeId)?.[0] || routeId;

// note: taken from here: https://github.com/Casumo/casumo-frontend/blob/master/web/mobile/src/js/native/NativeLayerMessenger.js
const metaData = { source: 'native-bridge' };

export class NativeBridge {
    constructor() {
        // if we're not running on native, bail out early
        if (!window.native) {
            throw Error('Not running on native');
        }
        if (!window.fabric.getGatekeeper) {
            throw Error('Gatekeeper should be available');
        }

        //setup api on native
        window.native.api ||= {
            navigateUsingRouteId: navigate,
            navigateTo: (url) => {
                // TODO: Remove console.log once native confirms all is fine
                console.log('NATIVE NAVIGATION REQUESTED FOR ... ', url);
                if (
                    url.indexOf('react-stack/navigation') > -1 ||
                    url.indexOf('gamelaunchers/navigation') > -1
                ) {
                    // Coming from navigation bubbler in iframe solution
                    navigate(ROUTE_IDS.GAMES_TOP);
                    return;
                }
                const fragmentOrAbsoluteUrl =
                    url.indexOf(window.location.origin) === 0
                        ? url.replace(window.location.origin, '')
                        : url;
                const prefix = window.native?.android ? '' : '/';
                const hasLeadingSlash = fragmentOrAbsoluteUrl.indexOf('/') === 0;
                const targetUrl = hasLeadingSlash
                    ? fragmentOrAbsoluteUrl
                    : `${prefix}${fragmentOrAbsoluteUrl}`;

                const possibleRouteId = Object.keys(KNOWN_ROUTES).find((r) =>
                    targetUrl.endsWith(r)
                );

                if (possibleRouteId) {
                    navigate(KNOWN_ROUTES[possibleRouteId]);
                } else {
                    goto(targetUrl);
                }
            },
            requestGameLaunch: (payload: unknown) => {
                try {
                    spineBus.publish('launch', {
                        kind: 'game-launch',
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        ...(payload as any)
                    });
                } catch (e) {
                    console.error('Error requesting game launch', e);
                }
            },
            // TODO: should fix types here.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            sendMessageToSpine: (topic: any, payload: unknown) => {
                spineBus.publish(topic, payload, metaData);
            },
            logout: () => {
                window.fabric
                    .getGatekeeper(GATEKEEPER_VERSION as 'v2.2.9', 'fabric-spine-v2')
                    .auth.logOut();
            },
            hideTopNav: () => {},
            restoreTopNav: () => {},

            registerOpenGameInGameRunner: () => {}
        };

        // Autorelay native events
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        spineBus.subscribe('native', (payload: any) => {
            // TODO: Remove console.log once native confirms all is fine
            console.log(`SENDING MESSAGE TO NATIVE`, JSON.stringify({ payload }));
            this.sendMessageToNative(camelCase(payload.kind), payload);
        });

        // Subscribe and handle events that native needs to know about
        // reflect onChange events on the native
        spineBus.subscribe('navigation', ({ kind, ...payload }: NavigationPayloads) => {
            switch (kind) {
                case 'changing':
                case 'changed': {
                    const { to } = payload as { to?: URL; from?: URL };
                    const result = to
                        ? getRouteFromPathname(to.pathname, get(page).data.navigationSlugs)
                        : undefined;
                    const id = findNativeRouteId(result?.page || '');
                    const pload = {
                        id,
                        isLoggedIn: get(page).data.isLoggedIn,
                        routeId: result?.page
                    };
                    spineBus.publish('native', {
                        kind: kind === 'changing' ? 'onRouteChanged' : 'onRouteLoaded',
                        ...pload
                    });
                    break;
                }
                default:
                    return;
            }
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        spineBus.subscribe('spine', (payload: any) => {
            if (payload.kind == 'loaded') {
                const { kind, ...mappedPayload } = payload;
                spineBus.publish('native', { kind: 'appLoaded', ...mappedPayload, id: '' });
            }
        });

        // relay the log out event
        spineBus.subscribe('player', ({ kind }) => {
            switch (kind) {
                case 'logged-out':
                    this.sendMessageToNative('playerLogout');
                    break;
                default:
                    return;
            }
        });
    }

    sendMessageToNative(kind: string, payload: object = {}) {
        // Wait for next frame. It solves issue we had when loader
        // was disappearing only to show frozen view underneath for
        // a second
        setTimeout(() => {
            if (window.casumoAndroidMessaging) {
                // Convert message object to JSON since JS objects cannot
                // be passed on to Android.
                window.casumoAndroidMessaging[kind](JSON.stringify(payload || {}));
            } else if (window.webkit?.messageHandlers) {
                window.webkit.messageHandlers[kind].postMessage(payload || '');
            } else {
                throw new Error(`Couldn't send message across: ${kind}:${JSON.stringify(payload)}`);
            }
        });
    }
}

type Unset = null | undefined;

type NativeApi = {
    sendMessageToSpine: (topic: unknown, payload: unknown) => void;
    navigateTo(url: string): void;
    requestGameLaunch: <K, T = K extends Payload<'launch-game'> ? NativePayloads : never>(
        payload: T
    ) => void;
    logout: VoidFunction;
    restoreTopNav: VoidFunction;
    hideTopNav: VoidFunction;
    registerOpenGameInGameRunner: VoidFunction;
    navigateUsingRouteId: typeof navigate;
};

declare global {
    interface Window {
        native:
            | {
                  api: NativeApi;
                  android: boolean | Unset; // set by the Android app only to identify that the client is the Android app
                  ios: boolean | Unset; // set by the iOS app only to identify that the client is the iOS app
                  //   loaded: false; // no idea what this does, this was before we joined and has always been there - need to check functionality from frontend
                  version: string | Unset; // version of the client (be it the iOS or Android app) - probably used to "toggle" things on/off due to backwards compatibility
                  biometrics: boolean | Unset; // this was used when there wasn't native authentication and we still supported biometric authentication
                  nativeIntercomEnabled: boolean | Unset; // this is a recently added flag, required to disable frontend Intercom since the apps provide the native implementation of the SDK
                  nativeNavigation:
                      | {
                            // this controls the navigation on the frontend - required to show/hide navigation components since the apps now have native navigation
                            showSidebarNav: boolean; // hides the side bar navigation
                            showTopNav: boolean; // hides the top navigation
                        }
                      | Unset;
              }
            | Unset;

        casumoAndroidMessaging: { [key: string]: (message: string) => void } | Unset;

        webkit:
            | { messageHandlers: { [key: string]: { postMessage: (message: object) => void } } }
            | Unset;
    }
}
